import { useCallback, useState, ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';

// ── Types ──

interface FileWithStatus extends File {
  uploadStatus?: 'pending' | 'uploading' | 'success' | 'error';
  fileType?: 'gpx' | 'media';
}

interface FileUploaderProps {
  bucketUrl: string;
  userFolder: string;
  selectedFormat: string;
  sessionName: string;
  sessionType: string;
  logoUrl?: string;
  onUploadComplete?: (files: File[], uploadId: string) => void;
}

// ── Helpers ──

const STATUS_TEXT: Record<string, string> = {
  uploading: 'Uploading...',
  success: 'Uploaded',
  error: 'Failed',
};

const STATUS_BADGE: Record<string, string> = {
  uploading: 'badge bg-primary',
  success: 'badge bg-success',
  error: 'badge bg-danger',
};

// ── Sub-components ──

function FileDropzone({
  onDrop,
  accept,
  label,
  icon,
  disabled,
  acceptedFormats,
}: {
  onDrop: (files: File[]) => void;
  accept: Record<string, string[]>;
  label: string;
  icon: ReactNode;
  disabled: boolean;
  acceptedFormats: string;
}) {
  const [rejectionMsg, setRejectionMsg] = useState('');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => {
      setRejectionMsg('');
      onDrop(accepted);
    },
    onDropRejected: () => {
      setRejectionMsg(`Unsupported file type. Accepted formats: ${acceptedFormats}`);
      setTimeout(() => setRejectionMsg(''), 10000);
    },
    multiple: true,
    accept,
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={`dropzone-box ${isDragActive ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
      >
        <input {...getInputProps()} />
        <div>
          {icon}
          {isDragActive ? (
            <p className="dropzone-active-label">Drop {label} here</p>
          ) : (
            <>
              <p className="dropzone-label">DROP {label.toUpperCase()} HERE</p>
              <p className="dropzone-sublabel">or click to select</p>
            </>
          )}
        </div>
      </div>
      {rejectionMsg && (
        <div className="dropzone-rejection">{rejectionMsg}</div>
      )}
    </div>
  );
}

function FileList({
  files,
  label,
  formatSize,
  onRemove,
  onClearAll,
  disabled,
}: {
  files: FileWithStatus[];
  label: string;
  formatSize: (file: File) => string;
  onRemove: (index: number) => void;
  onClearAll: () => void;
  disabled: boolean;
}) {
  if (files.length === 0) return null;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div className="file-list-header">
        <div className="file-list-count">
          {files.length} {label}{files.length !== 1 ? 's' : ''}
        </div>
        <button className="btn-clear" onClick={onClearAll} disabled={disabled}>
          Clear All
        </button>
      </div>
      <div className="file-list-scroll">
        {files.map((file, index) => (
          <div key={index} className="file-item">
            <div className="d-flex align-items-center justify-content-between">
              <div style={{ flex: 1, minWidth: 0, marginRight: '1rem' }}>
                <div className="file-name">{file.name}</div>
                <div className="file-meta">
                  {formatSize(file)}
                  <span
                    className={`ms-2 ${STATUS_BADGE[file.uploadStatus ?? ''] ?? 'badge bg-secondary'}`}
                    style={{ fontSize: '0.7rem' }}
                  >
                    {STATUS_TEXT[file.uploadStatus ?? ''] ?? 'Ready'}
                  </span>
                </div>
              </div>
              <button
                className="btn-remove"
                onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                disabled={file.uploadStatus === 'uploading'}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Icons ──

const GpsIcon = (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '1rem' }}>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
          fill="#999" stroke="#777" strokeWidth="1" />
    <circle cx="12" cy="9" r="2.5" fill="white" />
  </svg>
);

const MediaIcon = (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" style={{ marginBottom: '1rem' }}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

// ── Accepted file types ──

const GPX_ACCEPT = {
  '': ['.gpx'],
};

const MEDIA_ACCEPT = {
  '': ['.mp4', '.jpg', '.360', '.lrv', '.insv', '.zip', '.36P', '.insp'],
};

// ── Main component ──

export function FileUploader({
  bucketUrl,
  userFolder,
  selectedFormat,
  sessionName,
  sessionType,
  onUploadComplete,
}: FileUploaderProps) {
  const [gpxFiles, setGpxFiles] = useState<FileWithStatus[]>([]);
  const [mediaFiles, setMediaFiles] = useState<FileWithStatus[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const onDropGpx = useCallback((acceptedFiles: File[]) => {
    setGpxFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = acceptedFiles
        .filter((f) => !existingNames.has(f.name))
        .map((f) => Object.assign(f, { uploadStatus: 'pending' as const, fileType: 'gpx' as const }));
      return [...prev, ...newFiles];
    });
  }, []);

  const onDropMedia = useCallback((acceptedFiles: File[]) => {
    setMediaFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = acceptedFiles
        .filter((f) => !existingNames.has(f.name))
        .map((f) => Object.assign(f, { uploadStatus: 'pending' as const, fileType: 'media' as const }));
      return [...prev, ...newFiles];
    });
  }, []);

  // Helper function to chunk array into batches
  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Upload a set of files in parallel batches
  const uploadFileSet = async (
    files: FileWithStatus[],
    setFiles: React.Dispatch<React.SetStateAction<FileWithStatus[]>>,
    uploadId: string,
    uploaded: File[],
    totalFiles: number,
  ): Promise<boolean> => {
    let allOk = true;
    const batchSize = 20;
    const batches = chunkArray(files, batchSize);

    for (const batch of batches) {
      const uploadPromises = batch.map(async (file) => {
        const globalIndex = files.indexOf(file);

        setFiles((prev) => prev.map((f, idx) =>
          (idx === globalIndex ? { ...f, uploadStatus: 'uploading' as const } : f)
        ));

        try {
          const res = await fetch(`${bucketUrl}/${userFolder}/${uploadId}/input/${file.name}`, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
          });

          if (!res.ok) throw new Error(res.statusText);

          setFiles((prev) => prev.map((f, idx) =>
            (idx === globalIndex ? { ...f, uploadStatus: 'success' as const } : f)
          ));

          uploaded.push(file);
          setUploadProgress({ current: uploaded.length, total: totalFiles });

          return true;
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          allOk = false;

          setFiles((prev) => prev.map((f, idx) =>
            (idx === globalIndex ? { ...f, uploadStatus: 'error' as const } : f)
          ));

          return false;
        }
      });

      await Promise.all(uploadPromises);
    }

    return allOk;
  };

  const uploadFiles = async () => {
    setUploading(true);
    const uploadId = uuidv4();
    const totalFiles = gpxFiles.length + mediaFiles.length;
    setUploadProgress({ current: 0, total: totalFiles });
    const uploaded: File[] = [];

    const gpxOk = await uploadFileSet(gpxFiles, setGpxFiles, uploadId, uploaded, totalFiles);
    const mediaOk = await uploadFileSet(mediaFiles, setMediaFiles, uploadId, uploaded, totalFiles);

    if (gpxOk && mediaOk && uploaded.length === totalFiles) {
      try {
        await fetch(`${bucketUrl}/${userFolder}/${uploadId}/working/.keep`, {
          method: 'PUT',
          body: '',
          headers: { 'Content-Type': 'text/plain' },
        });

        await fetch(`${bucketUrl}/${userFolder}/${uploadId}/ready.json`, {
          method: 'PUT',
          body: JSON.stringify({
            data: {
              name: sessionName,
              type: sessionType,
              format: selectedFormat,
            }
          }, null, 2),
          headers: { 'Content-Type': 'application/json' },
        });
      } catch (err) {
        console.error('Failed to create ready.json or working folder:', err);
      }
    }

    setUploading(false);
    onUploadComplete?.(uploaded, uploadId);

    setTimeout(() => {
      setGpxFiles((prev) => prev.filter((f) => f.uploadStatus === 'error'));
      setMediaFiles((prev) => prev.filter((f) => f.uploadStatus === 'error'));
      setUploadProgress({ current: 0, total: 0 });
    }, 2000);
  };

  const allFiles = [...gpxFiles, ...mediaFiles];
  const progressPct = uploadProgress.total > 0
    ? Math.round((uploadProgress.current / uploadProgress.total) * 100)
    : 0;

  return (
    <div className="uploader-root">
      <div className="container" style={{ maxWidth: '1200px' }}>

        {/* Dropzones */}
        <div className="row mb-4" style={{ gap: '3rem' }}>
          <div className="col mb-4" style={{ flex: 1 }}>
            <h4 className="column-title">GPX Files</h4>
            <FileDropzone onDrop={onDropGpx} accept={GPX_ACCEPT} label="GPX files" icon={GpsIcon} disabled={uploading} acceptedFormats=".gpx" />
            <FileList
              files={gpxFiles}
              label="GPX file"
              formatSize={(f) => `${(f.size / 1024).toFixed(2)} KB`}
              onRemove={(i) => setGpxFiles((prev) => prev.filter((_, idx) => idx !== i))}
              onClearAll={() => setGpxFiles([])}
              disabled={uploading}
            />
          </div>

          <div className="col mb-4" style={{ flex: 1 }}>
            <h4 className="column-title">Media Files</h4>
            <FileDropzone onDrop={onDropMedia} accept={MEDIA_ACCEPT} label="media files" icon={MediaIcon} disabled={uploading} acceptedFormats=".jpg, .36P, .360, .mp4, .insp, .insv, .lrv, .zip" />
            <FileList
              files={mediaFiles}
              label="media file"
              formatSize={(f) => `${(f.size / 1024 / 1024).toFixed(2)} MB`}
              onRemove={(i) => setMediaFiles((prev) => prev.filter((_, idx) => idx !== i))}
              onClearAll={() => setMediaFiles([])}
              disabled={uploading}
            />
          </div>
        </div>

        {/* Upload button + progress */}
        {allFiles.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '2rem', borderBottom: '2px solid #222' }}>
            {uploading && (
              <div className="progress-section">
                <div className="progress-info">
                  <span>Uploading {uploadProgress.current} of {uploadProgress.total} files</span>
                  <span className="progress-pct">{progressPct}%</span>
                </div>
                <div className="progress-bar-custom">
                  <div className="progress-fill-custom" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            )}
            <button
              onClick={uploadFiles}
              disabled={uploading || allFiles.every((f) => f.uploadStatus === 'success')}
              className="btn-upload"
            >
              {uploading ? (<><span style={{ marginRight: '0.75rem' }}>⏳</span>Uploading Files...</>) : `Upload All Files (${allFiles.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
